import { useMemo } from 'react'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

/**
 * React Router `errorElement` for `/dashboard` — replaces the default
 * "Unexpected Application Error!" screen when a nested route throws.
 */
export function DashboardRouteErrorElement() {
  const error = useRouteError()
  const navigate = useNavigate()

  const message = useMemo(() => {
    if (isRouteErrorResponse(error)) {
      return `${error.status} ${error.statusText}`
    }
    if (error instanceof Error) return error.message
    try {
      return JSON.stringify(error)
    } catch {
      return String(error)
    }
  }, [error])

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-16"
      data-testid="dashboard-route-error"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          This page could not be displayed. You can return to the dashboard home or reload the page.
        </p>
        {import.meta.env.DEV && (
          <p className="mt-4 break-all text-left font-mono text-xs text-red-700/90">{message}</p>
        )}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            onClick={() => navigate('/dashboard', { replace: true })}
            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            Go to dashboard home
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  )
}
